import {useMemo, useState} from 'react';

/**
 * Period picker with RANGE selection.
 *
 * Click a chip to select that period. Click a second chip and the selection spans from
 * the first to the second — Q1 2025 then Q3 2026 gives you those seven quarters, in
 * either click order. A third click starts over. The anchor stays visibly marked while
 * a range is being built, because a picker that silently changes meaning on the next
 * click is worse than one with no range at all.
 *
 * Everything resolves to a {from, to} pair of `YYYY-MM` strings, which is also what the
 * manual fields set — so there is one selection model, not two competing ones.
 */
const quarterMonths = (year, q) => ({
	from: `${year}-${String((q - 1) * 3 + 1).padStart(2, '0')}`,
	to: `${year}-${String(q * 3).padStart(2, '0')}`,
});
const yearMonths = year => ({from: `${year}-01`, to: `${year}-12`});
const span = (a, b) => ({from: a.from < b.from ? a.from : b.from, to: a.to > b.to ? a.to : b.to});

export default function PeriodPicker({first, last, value, onChange}) {
	const [anchor, setAnchor] = useState(null);
	const firstYear = Number(first.slice(0, 4)), lastYear = Number(last.slice(0, 4));
	const years = useMemo(
		() => Array.from({length: lastYear - firstYear + 1}, (_, i) => firstYear + i),
		[firstYear, lastYear]);
	// Quarters for this year and last — the ones people actually reach for. Older
	// periods are reachable through the year chips and the manual fields.
	const quarterYears = years.filter(y => y >= lastYear - 1);

	const pick = period => {
		if (!anchor) { setAnchor(period); onChange(period); return; }
		onChange(span(anchor, period));
		setAnchor(null);
	};

	const isActive = p => value && value.from === p.from && value.to === p.to;
	const inRange = p => value && p.from >= value.from && p.to <= value.to;

	return (
		<div className="period">
			<div className="period__row">
				<button className="seg seg--wide" aria-pressed={!value}
					onClick={() => { setAnchor(null); onChange(null); }}>
					Visa allt ({firstYear}–)
				</button>
				{quarterYears.map(y => (
					<span className="period__group" key={y}>
						<span className="period__glabel">{y}</span>
						{[1, 2, 3, 4].map(q => {
							const p = quarterMonths(y, q);
							return (
								<button key={q} className="seg seg--q"
									aria-pressed={isActive(p) || (!!value && inRange(p))}
									data-anchor={anchor && anchor.from === p.from ? 'true' : undefined}
									onClick={() => pick(p)}>Q{q}</button>
							);
						})}
					</span>
				))}
			</div>
			<div className="period__row">
				<span className="period__glabel">År</span>
				{years.map(y => {
					const p = yearMonths(y);
					return (
						<button key={y} className="seg" aria-pressed={isActive(p)}
							data-anchor={anchor && anchor.from === p.from ? 'true' : undefined}
							onClick={() => pick(p)}>{y}</button>
					);
				})}
				<span className="period__manual">
					<label>Från
						<input type="month" value={value?.from ?? ''} max={value?.to ?? last}
							onChange={e => e.target.value && onChange({from: e.target.value, to: value?.to ?? last})} />
					</label>
					<label>Till
						<input type="month" value={value?.to ?? ''} min={value?.from ?? first}
							onChange={e => e.target.value && onChange({from: value?.from ?? first, to: e.target.value})} />
					</label>
				</span>
				{anchor && <span className="period__hint">Välj slutperiod…</span>}
			</div>
		</div>
	);
}
